import React from "react";
import {
	Box,
	Avatar,
	colors,
	CircularProgress,
	Typography,
	TextField,
	Divider,
	useMediaQuery,
	useTheme
} from "@mui/material";
import { useAppSelector } from "../../../store";
import { getFullState } from "../../../store/slices/auth/auth.slice";
import { useFormik } from "formik";
import { myAccountSchema } from "./schema";
import { useLazyFetchUserDataQuery, useUpdateUserAccountMutation } from "../../../store/services/auth/auth.service";
import { toast } from "react-toastify";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";
import ButtonComponent from "../../../Components/ButtonComponent";
const Personal = () => {
	const { user } = useAppSelector(getFullState);
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const [callUpdateAccount, { isLoading: isSaving }] = useUpdateUserAccountMutation();
	const [callWhoami] = useLazyFetchUserDataQuery();
	const {
		values: { firstName, lastName, email },
		handleChange,
		handleBlur,
		setFieldValue,
		errors,
		handleSubmit
	} = useFormik({
		initialValues: {
			firstName: user?.firstName || "",
			lastName: user?.lastName || "",
			email: user?.email || ""
		},
		validationSchema: myAccountSchema,
		onSubmit: values => {
			callUpdateAccount({
				body: {
					firstName: values.firstName.trim(),
					lastName: values.lastName.trim(),
					email: values.email.trim()
				}
			}).then(({ data, error }) => {
				if (data) {
					toast.success("Account has been updated successfully", { autoClose: 3000 });
					callWhoami();
				}
				if (error) {
					notifyError(error as IError);
				}
			});
		}
	});

	const handleTrimOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		handleBlur(e);
		setFieldValue(e.target.name, e.target.value.trim());
	};

	if (!user) {
		return <CircularProgress />;
	}
	return (
		<Box display="flex" flexDirection="column">
			<Box
				display="flex"
				alignItems={"center"}
				style={{ flexFlow: "row wrap", gridGap: 10, justifyContent: isSmallerScreen ? "center" : "flex-start" }}
			>
				<Avatar
					sx={{
						bgcolor: colors.deepOrange[500],
						fontSize: isSmallerScreen ? "40px" : "60px",
						fontWeight: "bold",
						width: isSmallerScreen ? 70 : 120,
						height: isSmallerScreen ? 70 : 120
					}}
				>
					{user.firstName[0]}
				</Avatar>
				<Box ml={2}>
					<Typography
						variant={isSmallerScreen ? "h6" : "h4"}
						fontWeight={"bold"}
						textAlign={isSmallerScreen ? "center" : "start"}
					>
						{user.firstName + " " + user.lastName}
					</Typography>
					<Typography
						variant={"subtitle1"}
						color={"textSecondary"}
						noWrap={false}
						textAlign={isSmallerScreen ? "center" : "start"}
						style={{ wordBreak: "break-all" }}
					>
						{user.email}
					</Typography>
				</Box>
			</Box>
			<Box my={3}>
				<Divider />
			</Box>
			<form onSubmit={handleSubmit}>
				<Box mt={2} display={"flex"} flexDirection="column" sx={{ width: isSmallerScreen ? "100%" : "50%" }}>
					<Box display={"flex"} mb={2}>
						<Box mr={3} width={"100%"}>
							<Typography variant={"subtitle1"} fontWeight={"bold"}>
								First Name
							</Typography>
							<TextField
								name={"firstName"}
								onChange={handleChange}
								onBlur={handleTrimOnBlur}
								error={!!errors.firstName}
								helperText={errors.firstName}
								fullWidth
								value={firstName}
							/>
						</Box>
						<Box width={"100%"}>
							<Typography variant={"subtitle1"} fontWeight={"bold"}>
								Last Name
							</Typography>
							<TextField
								name={"lastName"}
								onChange={handleChange}
								onBlur={handleTrimOnBlur}
								error={!!errors.lastName}
								helperText={errors.lastName}
								fullWidth
								value={lastName}
							/>
						</Box>
					</Box>
					<Box mb={2}>
						<Typography variant={"subtitle1"} fontWeight={"bold"}>
							Email
						</Typography>
						<TextField
							type={"email"}
							name={"email"}
							onChange={handleChange}
							onBlur={handleTrimOnBlur}
							error={!!errors.email}
							helperText={errors.email}
							fullWidth
							value={email}
						/>
					</Box>
					<Box mt={4}>
						<ButtonComponent
							fullWidth
							variant={"contained"}
							color={"primary"}
							type="submit"
							disabled={isSaving}
							text={"Save"}
							loading={isSaving}
						/>
					</Box>
				</Box>
			</form>
		</Box>
	);
};

export default Personal;
